import React, { useCallback, useEffect, useMemo, useState } from "react";
import Common from "us.common";
import { Formik } from "formik";
import { connect, ConnectedProps } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import _, { startCase } from "lodash";

import {
  WeekendConfigurations,
  NonExecutionDays,
  NonExecutionDay,
} from "./components";
import {
  CalendarOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
} from "us.icons";
import {
  NonProductionDayDrawerType,
  NonProductionDayTabs,
  NonProductionDayType,
  RedDayCriteriaMap,
  YearNavigation,
} from "us.collection.admin/constants";
import { RootState } from "us.helper/types";
import { nonProductionDays_New } from "us.collection.admin/actions";
import "./NonProductionDays.scss";
import { NonProductionDay } from "us.collection.admin/interfaces";
import {
  getRedDayInitialValues,
  redDayToCriteria,
} from "us.collection.admin/functions";
import { handleEndPointLocals } from "us.common/functions";
import { getFilteredRedDays } from "./functions";
import { useSafeBackNavigation } from "us.helper/navigation";

const {
  $Tabs,
  $Button,
  $PageHeader,
  $Divider,
  $Affix,
  $DatePicker,
  $Tooltip,
  $Col,
  $Row,
  $Space,
  $Drawer,
} = Common.Components;

/**
 * @description Non Productions Days Home Component
 * @link Design document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3049914453/Non+Email+Days+UI+Design
 * @author Rajitha Sanjayamal <rajithasa@unicorn-solutions.com>
 * @since 21/07/2022
 */
const NonProductionsDays: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useSafeBackNavigation();

  const {
    selectedCriteria,
    redDays,
    weekendConfigurations,
    manageTabs,
    getNonProductionDays,
    removeRedDay,
    resetCopyCalendarData,
  } = props;

  const targetYear = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const y = sp.get("year");
    const parsed = y ? parseInt(y, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : dayjs().year();
  }, [location.search]);

  const isCopyEnabled = useMemo(() => {
    try {
      const currentYear = dayjs().year(); // Get the current year
      const nextYear = currentYear + 1;
      const isCurrentYear = currentYear === targetYear; // Compares only the year
      const isNextYear = nextYear === targetYear; // Compares only the year
      return isCurrentYear || isNextYear;
    } catch (error) {
      return false;
    }
  }, [targetYear]);

  const [isOpenYearPicker, setIsOpenYearPicker] = useState<boolean>(false);
  const [drawerDetail, setDrawerDetail] = useState<{
    isVisible: boolean;
    values: NonProductionDay;
    type: NonProductionDayDrawerType;
  }>({
    isVisible: false,
    values: getRedDayInitialValues(selectedCriteria, targetYear),
    type: NonProductionDayDrawerType.NONE,
  });

  useEffect(() => {
    getNonProductionDays && getNonProductionDays({ year: `${targetYear}` });
  }, [targetYear]);

  useEffect(() => {
    if (
      typeof weekendConfigurations?.data === "boolean" &&
      weekendConfigurations.data
    ) {
      closeDrawer();
    }
  }, [weekendConfigurations.data]);

  const filterRedDaysByCriteria = useCallback(
    (criteria: any, year: any) => {
      return getFilteredRedDays(redDays.data, criteria, year);
    },
    [redDays.data, selectedCriteria],
  );

  /**
   * @function
   * @description handle Tab Click
   * @param {string} activeKey current tab
   */
  const handleTab = (activeKey: string) => {
    manageTabs && manageTabs({ selectedCriteria: activeKey });
  };

  /**
   * @function
   * @description handle year navigation
   * @param {string} step step type (next, previous)
   */
  const handleYearNavigation = (
    step: string,
    setFieldValue: any,
    currentYear: any,
  ) => {
    let selectedYear = currentYear;
    if (step === YearNavigation.NEXT) {
      selectedYear = (parseInt(selectedYear) + 1).toString();
    } else {
      selectedYear = (parseInt(selectedYear) - 1).toString();
    }
    setFieldValue("selectedYear", selectedYear);
    navigate("/non-production-days?year=" + selectedYear, { replace: true });
  };

  /**
   * @function
   * @description handle year selection
   * @param {string} year selected year
   */
  const handleYearSelection = (year: string, setFieldValue: any) => {
    setFieldValue("selectedYear", year);
    getNonProductionDays && getNonProductionDays({ year: year });
  };

  /**
   * @function
   * @description open drawer
   * @param {boolean} open
   * @param {any} values
   */
  const openDrawer = (open: boolean, values: any) => {
    const initialValues = getRedDayInitialValues(selectedCriteria, targetYear);
    const mappedValues: NonProductionDay | null = values
      ? {
          ...initialValues,
          id: values.id,
          reason: values.reason,
          isRecurring: !!values.isRecurring,
          dateRange: [dayjs(values.startDate), dayjs(values.endDate)],
          criteria: redDayToCriteria(values, selectedCriteria),
        }
      : initialValues;

    const isOldDay = dayjs().isAfter(dayjs(values?.endDate), "day");

    setDrawerDetail &&
      setDrawerDetail({
        isVisible: open,
        values: mappedValues ? mappedValues : initialValues,
        type: isOldDay
          ? NonProductionDayDrawerType.VIEW
          : values?.id
          ? NonProductionDayDrawerType.EDIT
          : NonProductionDayDrawerType.ADD,
      });
  };

  const closeDrawer = () => {
    setDrawerDetail &&
      setDrawerDetail({
        ...drawerDetail,
        isVisible: false,
      });
  };

  /**
   * @function
   * @description get drawer title
   * @param {NonProductionDay} values
   * @returns {string}
   */
  const getDrawerTitle = (values: NonProductionDay): string => {
    try {
      if (drawerDetail.type === NonProductionDayDrawerType.WEEKEND_CONFIG) {
        return t(
          "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.WEEKEND_CONFIGURATIONS",
        );
      } else {
        const { id, reason } = values;
        const title =
          reason?.length > 60
            ? startCase(reason).substring(0, 60) + "..."
            : startCase(reason);
        if (drawerDetail.type === NonProductionDayDrawerType.VIEW) {
          return `${title}`;
        } else {
          return id > 0
            ? `${handleEndPointLocals("US.COMMON:COMMON.EDIT")} : ${title}`
            : handleEndPointLocals("US.COLLECTION.COMMON:COMMON.ADD_NEW");
        }
      }
    } catch (error) {
      return "";
    }
  };

  return (
    <Formik
      initialValues={{
        selectedYear: targetYear,
      }}
      onSubmit={() => {}}
      enableReinitialize
    >
      {({ values, setFieldValue }: any) => (
        <div className="non-production-days space-content">
          {/* Header content */}
          <$Affix offsetTop={48}>
            <div className="page-header header-border">
              <div className="d-flex flex-row align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <$PageHeader
                    className="px-0"
                    onBack={goBack}
                    title={t(
                      "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.NON_PRODUCTION_DAYS",
                    )}
                  />
                  <$Divider className="bui-devider" type="vertical" />

                  <div className="ml-3">
                    <div className="year-picker-custom d-flex align-items-center">
                      <$DatePicker
                        className="year-pick"
                        name="selectedYear"
                        picker="year"
                        open={isOpenYearPicker}
                        onChange={(date: any) => {
                          handleYearSelection(
                            date.year().toString(),
                            setFieldValue,
                          );
                        }}
                        popupClassName={"year-picker-custom"}
                        onOpenChange={(open: boolean) =>
                          setIsOpenYearPicker(open)
                        }
                      />
                      <$Button
                        size="small"
                        onClick={() =>
                          handleYearNavigation(
                            YearNavigation.PREVIOUS,
                            setFieldValue,
                            values?.selectedYear,
                          )
                        }
                      >
                        <LeftOutlined />
                      </$Button>

                      <div className="px-4 current-year">
                        <strong>{values?.selectedYear}</strong>
                      </div>
                      <$Button
                        size="small"
                        onClick={() =>
                          handleYearNavigation(
                            YearNavigation.NEXT,
                            setFieldValue,
                            values?.selectedYear,
                          )
                        }
                      >
                        <RightOutlined />
                      </$Button>
                      <$Button
                        size="small"
                        className="ml-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isOpenYearPicker) {
                            setIsOpenYearPicker(true);
                          }
                        }}
                      >
                        <CalendarOutlined />
                      </$Button>
                    </div>
                  </div>
                  <$Divider className="bui-devider" type="vertical" />
                  <div className="d-flex align-items-center">
                    <$Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => openDrawer(true, null)}
                      disabled={dayjs().year() > values?.selectedYear}
                    >
                      {t("US.COLLECTION.COMMON:COMMON.ADD_NEW")}
                    </$Button>
                  </div>
                </div>

                <div className="d-flex align-items-center">
                  <$Space size={16}>
                    <$Button
                      onClick={() =>
                        setDrawerDetail({
                          isVisible: true,
                          type: NonProductionDayDrawerType.WEEKEND_CONFIG,
                          values: null as any,
                        })
                      }
                      disabled={dayjs().year() > values?.selectedYear}
                    >
                      {t(
                        "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.CONFIGURE_WEEKENDS",
                      )}
                    </$Button>

                    <$Button
                      type="primary"
                      onClick={() => {
                        resetCopyCalendarData && resetCopyCalendarData({});
                        navigate(
                          `/non-production-days/copy-previous-year?year=${values?.selectedYear}`,
                        );
                      }}
                      disabled={!isCopyEnabled}
                    >
                      {t(
                        "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.COPY_FROM_PREVIOUS_YEAR",
                      )}
                    </$Button>
                  </$Space>
                </div>
              </div>
            </div>
          </$Affix>

          {/* Tab content */}
          <div className="mb-5">
            <$Row gutter={16}>
              <$Col xl={{ span: 24 }} xxl={{ span: 24 }}>
                <$Tabs
                  className="bui-tabs"
                  defaultActiveKey={NonProductionDayType.Non_Printing}
                  activeKey={selectedCriteria}
                  onChange={(activeKey: string) => handleTab(activeKey)}
                  items={NonProductionDayTabs.map(
                    ({
                      name,
                      description,
                      type,
                    }: typeof NonProductionDayTabs[0]) => ({
                      key: type,
                      label: (
                        <$Tooltip placement="top" title={t(description)}>
                          {`${t(name)} (${
                            filterRedDaysByCriteria(type, values?.selectedYear)
                              .length
                          })`}
                        </$Tooltip>
                      ),
                      children: (
                        <NonExecutionDays
                          onView={(record: NonProductionDay) => {
                            openDrawer(true, record);
                          }}
                          onDelete={(record: NonProductionDay) => {
                            removeRedDay &&
                              removeRedDay(record, selectedCriteria);
                          }}
                        />
                      ),
                    }),
                  )}
                />
              </$Col>
            </$Row>
          </div>

          <$Drawer
            title={getDrawerTitle(drawerDetail.values)}
            width={650}
            open={drawerDetail.isVisible}
            onClose={closeDrawer}
            destroyOnClose
          >
            {drawerDetail.type ===
              NonProductionDayDrawerType.WEEKEND_CONFIG && (
              <WeekendConfigurations onClose={closeDrawer} />
            )}
            {[
              NonProductionDayDrawerType.ADD,
              NonProductionDayDrawerType.EDIT,
              NonProductionDayDrawerType.VIEW,
            ].includes(drawerDetail.type) && (
              <NonExecutionDay
                type={drawerDetail.type}
                selectedCriteria={RedDayCriteriaMap[selectedCriteria]}
                values={drawerDetail.values}
                onClose={closeDrawer}
              />
            )}
          </$Drawer>
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { nonProductionDays } = state;
  const { selectedCriteria, redDays, weekendConfigurations } =
    nonProductionDays;
  return {
    selectedCriteria,
    redDays,
    weekendConfigurations,
  };
};

const { calendar, redDays, copiedCalendarData } = nonProductionDays_New;

const mapDispatchToProps = {
  manageTabs: calendar.manageTabs,
  updateNonProduction: calendar.update,
  getNonProductionDays: redDays.get,
  removeRedDay: redDays.remove,
  resetCopyCalendarData: copiedCalendarData.reset,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(NonProductionsDays);
